/**
 * @fileoverview 休暇管理（有休概念なし・乗下船ベース）
 * @description 3タブ構成:
 *   ① 休暇残（過不足）: 期間内の 乗船日数・取得休暇・規定休暇・過不足 を全乗組員分。印刷対応。
 *   ② 希望休の承認: 船員から届いた希望休申請の承認/却下。管理者が休暇実績を直接登録も可。
 *   ③ 規定設定: 月あたり規定休日数（過不足の基準）。
 */
import React, { useState, useEffect, useCallback } from 'react';
import { CalendarDays } from 'lucide-react';
import { get, post, put } from '../services/api';
import SearchableSelect from '../components/SearchableSelect';
import { SmartDateInput } from '../components/SmartDateTime';

interface CrewUser { id: string; user_name: string; user_role: string; vessel_name?: string | null }
interface BalanceRow {
  user_id: string; user_name: string; vessel_name: string | null;
  aboard_days: number; shore_work_days: number; rest_days: number; planned_days: number;
  required_days: number; required_total: number; diff_days: number; projected_diff: number;
}
interface RestRequest {
  id: string; user_id: string; user_name: string; vessel_name: string | null;
  request_type: string; start_date: string; end_date: string; days: number;
  reason: string | null; status: string; review_comment: string | null;
  reviewed_by_name: string | null; reviewed_at: string | null; created_at: string;
}

type Tab = 'balance' | 'requests' | 'policy';
const CURRENT_YEAR = new Date().getFullYear();
const fmtDate = (v?: string | null) => (v ? String(v).slice(0, 10) : '-');

const STATUS_META: Record<string, { label: string; bg: string; fg: string; border: string }> = {
  pending:   { label: '承認待ち', bg: '#fffbeb', fg: '#d97706', border: '#fde68a' },
  approved:  { label: '承認済み', bg: '#f0fdf4', fg: '#16a34a', border: '#bbf7d0' },
  rejected:  { label: '却下',     bg: '#fef2f2', fg: '#dc2626', border: '#fecaca' },
  cancelled: { label: '取り下げ', bg: '#f1f5f9', fg: '#64748b', border: '#e2e8f0' },
};

function crewOptions(crew: CrewUser[]): { value: string; label: string; group: string }[] {
  const UNASSIGNED = '未配属';
  return crew
    .filter(c => !['system_admin', 'shore_admin'].includes(c.user_role))
    .map(c => ({ value: c.id, label: c.user_name, group: c.vessel_name || UNASSIGNED }))
    .sort((a, b) => a.group !== b.group
      ? (a.group === UNASSIGNED ? 1 : b.group === UNASSIGNED ? -1 : a.group.localeCompare(b.group, 'ja'))
      : a.label.localeCompare(b.label, 'ja'));
}

const LeavePage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('balance');
  const [crew, setCrew] = useState<CrewUser[]>([]);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    get<any>('/users', { limit: '500' }).then(r => setCrew((r.users ?? r.data ?? []) as CrewUser[])).catch(() => {});
    get<any>('/leave/rest-pending-count').then(r => setPendingCount(r.data?.count ?? 0)).catch(() => {});
  }, []);

  const tabBtn = (t: Tab, label: string, badge?: number) => (
    <button onClick={() => setTab(t)} style={{
      padding: '7px 18px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700,
      display: 'inline-flex', alignItems: 'center', gap: 7,
      background: tab === t ? '#fff' : 'transparent', color: tab === t ? '#0f172a' : '#64748b',
      boxShadow: tab === t ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
    }}>
      {label}
      {!!badge && badge > 0 && (
        <span style={{ background: '#f59e0b', color: '#fff', borderRadius: 999, fontSize: 11, fontWeight: 800, padding: '1px 7px' }}>{badge}</span>
      )}
    </button>
  );

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <h1 className="page-title">休暇管理</h1>
      </div>
      <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: 13 }}>
        乗下船ベースの休暇管理です。船員からの希望休を承認し、規定休日数に対する<strong>過不足</strong>を確認できます（有給・付与の概念はありません）。
      </p>

      <div style={{ display: 'inline-flex', background: '#F1F5F9', borderRadius: 10, padding: 3, gap: 2, marginBottom: 20 }}>
        {tabBtn('balance', '休暇残（過不足）')}
        {tabBtn('requests', '希望休の承認', pendingCount)}
        {tabBtn('policy', '規定設定')}
      </div>

      {tab === 'balance' && <BalanceTab />}
      {tab === 'requests' && <RequestsTab crew={crew} onCountChange={setPendingCount} />}
      {tab === 'policy' && <PolicyTab />}
    </div>
  );
};

// ── ① 休暇残（過不足）タブ ───────────────────────────────────────────
const diffCell = (d: number) => (
  <span style={{ fontWeight: 800, color: d < 0 ? '#dc2626' : d > 0 ? '#16a34a' : '#64748b' }}>
    {d < 0 ? '▼' : d > 0 ? '△' : ''}{d}日
  </span>
);

const BalanceTab: React.FC = () => {
  const [from, setFrom] = useState(`${CURRENT_YEAR}-01-01`);
  const [to, setTo] = useState(new Date(Date.now() + 9 * 3600000).toISOString().slice(0, 10));
  const [rows, setRows] = useState<BalanceRow[]>([]);
  const [meta, setMeta] = useState<{ required_days: number; required_total: number; monthly_required_days: number; has_future: boolean; policy_days: number; policy_period_months: number }>(
    { required_days: 0, required_total: 0, monthly_required_days: 9, has_future: false, policy_days: 9, policy_period_months: 1 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await get<any>('/leave/rest-balance', { from, to });
      setRows((r.data?.rows ?? []) as BalanceRow[]);
      setMeta({
        required_days: r.data?.required_days ?? 0, required_total: r.data?.required_total ?? 0,
        monthly_required_days: r.data?.monthly_required_days ?? 9, has_future: !!r.data?.has_future,
        policy_days: r.data?.policy_days ?? 9, policy_period_months: r.data?.policy_period_months ?? 1,
      });
    } catch { setRows([]); }
    setLoading(false);
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const future = meta.has_future;
  // 過不足の少ない順（不足が上）。未来を含む場合は見込みで並べる。
  const sorted = [...rows].sort((a, b) => (future ? a.projected_diff - b.projected_diff : a.diff_days - b.diff_days));

  return (
    <>
      <div className="flex-row" style={{ marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end', gap: 12 }}>
        <div className="form-g" style={{ margin: 0 }}>
          <label className="form-l">期間（開始）</label>
          <SmartDateInput value={from} onChange={setFrom} />
        </div>
        <div className="form-g" style={{ margin: 0 }}>
          <label className="form-l">期間（終了）</label>
          <SmartDateInput value={to} onChange={setTo} />
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ color: '#64748b', fontSize: 12 }}>
            規定 {meta.policy_days}日/{meta.policy_period_months === 12 ? '年' : `${meta.policy_period_months}ヶ月`}（≒月{meta.monthly_required_days}日）→ 実績 {meta.required_days}日
          </span>
        </div>
      </div>

      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12.5, color: '#1e3a8a', lineHeight: 1.7 }}>
        <strong>休暇は自動集計です（個別入力不要）。</strong>休暇(実績) = 非乗船日数 −<strong> 陸上勤務日数</strong>。バーは<strong>取得休暇の規定に対する割合</strong>で、点線が規定ライン。
        <strong style={{ color: '#dc2626' }}>赤は休みが足りない</strong>、<strong style={{ color: '#16a34a' }}>緑は規定以上</strong>。
        {future && <>　終了日を未来にすると承認済み・申請中の<strong>希望休（予定休）</strong>を淡色で重ねて見込みも表示します。</>}
      </div>

      {loading ? <div className="loading"><div className="spinner" /></div> : sorted.length === 0 ? (
        <div className="card"><div className="empty" style={{ padding: 40 }}>対象データがありません</div></div>
      ) : (() => {
        const short = sorted.filter(r => r.diff_days < 0).length;
        const over = sorted.filter(r => r.diff_days > 0).length;
        const okc = sorted.length - short - over;
        const chip = (n: number, label: string, color: string, bg: string) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, background: bg, border: `1px solid ${color}33`, borderRadius: 10, padding: '8px 14px' }}>
            <span style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>{n}</span>
            <span style={{ fontSize: 12, color: '#475569', fontWeight: 700 }}>{label}</span>
          </div>
        );
        return (
          <>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              {chip(short, '休みが不足', '#dc2626', '#fef2f2')}
              {chip(okc, 'ほぼ規定どおり', '#64748b', '#f8fafc')}
              {chip(over, '規定以上', '#16a34a', '#f0fdf4')}
            </div>
            <div className="card" style={{ padding: '6px 0' }}>
              {sorted.map((r, i) => {
                const max = Math.max(r.rest_days, r.required_days, r.rest_days + (future ? r.planned_days : 0), 1);
                const restPct = (r.rest_days / max) * 100;
                const plannedPct = future ? (r.planned_days / max) * 100 : 0;
                const reqPct = (r.required_days / max) * 100;
                const under = r.diff_days < 0;
                const fill = under ? '#f59e0b' : '#22c55e';
                return (
                  <div key={r.user_id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '11px 18px', borderTop: i === 0 ? 'none' : '1px solid #f1f5f9' }}>
                    <div style={{ width: 150, flexShrink: 0, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.user_name}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.vessel_name ?? '下船中'}・乗船{r.aboard_days}日{r.shore_work_days > 0 ? `・陸上勤務${r.shore_work_days}日` : ''}
                      </div>
                    </div>
                    {/* バー */}
                    <div style={{ flex: 1, minWidth: 120, position: 'relative', height: 22, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden' }}
                      title={`取得 ${r.rest_days}日 / 規定 ${r.required_days}日`}>
                      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(restPct, 100)}%`, background: fill, transition: 'width .2s' }} />
                      {future && r.planned_days > 0 && (
                        <div style={{ position: 'absolute', left: `${Math.min(restPct, 100)}%`, top: 0, bottom: 0, width: `${Math.min(plannedPct, 100 - Math.min(restPct, 100))}%`, background: fill, opacity: 0.35 }} title={`予定休 ${r.planned_days}日`} />
                      )}
                      {/* 規定ライン */}
                      <div style={{ position: 'absolute', left: `calc(${Math.min(reqPct, 100)}% - 1px)`, top: -2, bottom: -2, width: 2, background: '#334155' }} />
                      <div style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 11, fontWeight: 800, color: restPct > 22 ? '#fff' : '#334155' }}>
                        取得 {r.rest_days}日{future && r.planned_days > 0 ? `（+予定${r.planned_days}）` : ''}
                      </div>
                    </div>
                    <div style={{ width: 92, flexShrink: 0, textAlign: 'right' }}>
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>規定 {r.required_days}日</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: under ? '#dc2626' : r.diff_days > 0 ? '#16a34a' : '#64748b' }}>
                        {diffCell(future ? r.projected_diff : r.diff_days)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        );
      })()}
    </>
  );
};

// ── ② 希望休の承認タブ ──────────────────────────────────────────────
const RequestsTab: React.FC<{ crew: CrewUser[]; onCountChange: (n: number) => void }> = ({ crew, onCountChange }) => {
  const [rows, setRows] = useState<RestRequest[]>([]);
  const [filter, setFilter] = useState<'pending' | 'all'>('pending');
  const [loading, setLoading] = useState(true);
  const [review, setReview] = useState<{ row: RestRequest; action: 'approve' | 'reject' } | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await get<any>('/leave/rest-requests', filter === 'pending' ? { status: 'pending' } : {});
      setRows((r.data ?? []) as RestRequest[]);
    } catch { setRows([]); }
    setLoading(false);
    try { const c = await get<any>('/leave/rest-pending-count'); onCountChange(c.data?.count ?? 0); } catch { /* ignore */ }
  }, [filter, onCountChange]);
  useEffect(() => { load(); }, [load]);

  const submitReview = async () => {
    if (!review) return;
    setBusy(true);
    try {
      await put(`/leave/rest-requests/${review.row.id}`, { action: review.action, comment: comment.trim() || undefined });
      setReview(null); setComment(''); load();
    } catch (e: any) { alert(e?.message ?? '処理に失敗しました'); }
    setBusy(false);
  };

  return (
    <>
      <div className="flex-row" style={{ marginBottom: 16, alignItems: 'center', gap: 12 }}>
        <div style={{ display: 'inline-flex', background: '#F1F5F9', borderRadius: 8, padding: 3, gap: 2 }}>
          {(['pending', 'all'] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: '6px 14px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700,
              background: filter === f ? '#fff' : 'transparent', color: filter === f ? '#0f172a' : '#64748b',
            }}>{f === 'pending' ? '承認待ち' : 'すべて'}</button>
          ))}
        </div>
        <button className="btn-p" style={{ marginLeft: 'auto' }} onClick={() => setShowAdd(true)}>＋休暇/陸上勤務を登録</button>
      </div>

      {loading ? <div className="loading"><div className="spinner" /></div> : (
        <div className="card" style={{ padding: 0, overflow: 'auto' }}>
          <table>
            <thead>
              <tr><th>乗組員</th><th>船舶</th><th>種別</th><th>期間</th><th>日数</th><th>理由</th><th>状態</th><th>操作</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={8} className="empty">{filter === 'pending' ? '承認待ちの希望休はありません' : '申請はありません'}</td></tr>
              ) : rows.map(r => {
                const st = STATUS_META[r.status] ?? STATUS_META['pending']!;
                return (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{r.user_name}</td>
                    <td>{r.vessel_name ?? '-'}</td>
                    <td>{r.request_type === 'shore_work' ? '🏢 陸上勤務' : r.request_type === 'leave' ? '休暇実績' : '🌴 希望休'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.start_date)} 〜 {fmtDate(r.end_date)}</td>
                    <td>{r.days}日</td>
                    <td style={{ maxWidth: 220, color: '#475569' }}>{r.reason || '-'}{r.review_comment ? <span style={{ color: '#94a3b8' }}>／{r.review_comment}</span> : ''}</td>
                    <td><span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: st.bg, color: st.fg, border: `1px solid ${st.border}` }}>{st.label}</span></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {r.status === 'pending' ? (
                        <span style={{ display: 'inline-flex', gap: 6 }}>
                          <button className="btn-o" style={{ padding: '5px 12px', fontSize: 12, color: '#16a34a', borderColor: '#bbf7d0' }} onClick={() => { setReview({ row: r, action: 'approve' }); setComment(''); }}>承認</button>
                          <button className="btn-o" style={{ padding: '5px 12px', fontSize: 12, color: '#dc2626', borderColor: '#fecaca' }} onClick={() => { setReview({ row: r, action: 'reject' }); setComment(''); }}>却下</button>
                        </span>
                      ) : <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {review && (
        <div className="modal-overlay" onClick={() => setReview(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">{review.action === 'approve' ? '希望休を承認' : '希望休を却下'}</div>
            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 12px' }}>
              {review.row.user_name} さんの {fmtDate(review.row.start_date)}〜{fmtDate(review.row.end_date)} の希望休を
              {review.action === 'approve' ? '承認します。' : '却下します。'}結果は申請者へ通知されます。
            </p>
            <div className="form-g">
              <label className="form-l">コメント（任意・申請者に通知）</label>
              <textarea className="form-i" rows={2} value={comment} onChange={e => setComment(e.target.value)} />
            </div>
            <div className="flex-row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn-o" onClick={() => setReview(null)}>キャンセル</button>
              <button className="btn-p" onClick={submitReview} disabled={busy}
                style={review.action === 'reject' ? { background: '#dc2626', borderColor: '#dc2626' } : undefined}>
                {busy ? '処理中…' : review.action === 'approve' ? '承認する' : '却下する'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAdd && <AddLeaveModal crew={crew} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </>
  );
};

// 管理者が休暇実績/陸上勤務を直接登録（承認済みで作成）
const AddLeaveModal: React.FC<{ crew: CrewUser[]; onClose: () => void; onSaved: () => void }> = ({ crew, onClose, onSaved }) => {
  const [userId, setUserId] = useState('');
  const [kind, setKind] = useState<'leave' | 'shore_work'>('leave');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!userId) { alert('対象船員を選択してください'); return; }
    if (!start || !end) { alert('開始日・終了日を入力してください'); return; }
    setSaving(true);
    try {
      await post('/leave/rest-requests', { user_id: userId, request_type: kind === 'shore_work' ? 'shore_work' : 'desired', start_date: start, end_date: end, reason: reason || undefined, direct: true });
      onSaved();
    } catch (e: any) { alert(e?.message ?? '登録に失敗しました'); setSaving(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">休暇 / 陸上勤務の登録</div>
        <div className="form-g">
          <label className="form-l">種別</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {([['leave', '🌴 休暇', '休みに数える'], ['shore_work', '🏢 陸上勤務', '勤務扱い（休みに数えない）']] as const).map(([k, lbl, d]) => (
              <button key={k} onClick={() => setKind(k)} style={{ flex: 1, padding: '10px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                border: kind === k ? '2px solid #0066CC' : '1.5px solid #e2e8f0', background: kind === k ? '#eff6ff' : '#fff' }}>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: kind === k ? '#0066CC' : '#1e293b' }}>{lbl}</div>
                <div style={{ fontSize: 10.5, color: '#94a3b8' }}>{d}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="form-g">
          <label className="form-l">対象船員</label>
          <SearchableSelect value={userId} onChange={setUserId} options={crewOptions(crew)} placeholder="氏名で検索・船ごとに選択..." />
        </div>
        <div className="flex-row" style={{ alignItems: 'flex-end' }}>
          <div className="form-g" style={{ flex: 1 }}><label className="form-l">開始日</label><SmartDateInput value={start} onChange={setStart} /></div>
          <div className="form-g" style={{ flex: 1 }}><label className="form-l">終了日</label><SmartDateInput value={end} onChange={setEnd} /></div>
        </div>
        <div className="form-g">
          <label className="form-l">メモ</label>
          <textarea className="form-i" rows={2} value={reason} onChange={e => setReason(e.target.value)} />
        </div>
        <div className="flex-row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn-o" onClick={onClose}>キャンセル</button>
          <button className="btn-p" onClick={submit} disabled={saving}>{saving ? '登録中…' : '登録する'}</button>
        </div>
      </div>
    </div>
  );
};

// ── ③ 規定設定タブ（Xヶ月ごとにY日 / 年間で指定） ─────────────────────
const PERIOD_OPTIONS = [
  { v: 1, label: '1ヶ月あたり' },
  { v: 3, label: '3ヶ月あたり' },
  { v: 6, label: '6ヶ月あたり' },
  { v: 12, label: '1年（12ヶ月）あたり' },
];
const PolicyTab: React.FC = () => {
  const [days, setDays] = useState('9');
  const [period, setPeriod] = useState(1);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    get<any>('/leave/rest-policy').then(r => {
      setDays(String(r.data?.required_days ?? 9));
      setPeriod(Number(r.data?.period_months ?? 1));
    }).catch(() => {});
  }, []);
  const perMonth = period > 0 && Number(days) >= 0 ? Math.round((Number(days) / period) * 100) / 100 : 0;
  const perYear = Math.round(perMonth * 12 * 10) / 10;
  const save = async () => {
    setSaving(true);
    try {
      await put('/leave/rest-policy', { required_days: Number(days), period_months: period });
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e: any) { alert(e?.message ?? '保存に失敗しました'); }
    setSaving(false);
  };
  return (
    <div className="card" style={{ maxWidth: 560, padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <CalendarDays size={18} color="#0066CC" />
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>規定休日数</h2>
      </div>
      <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 18px' }}>
        過不足の基準になる規定休日数です。<strong>「◯ヶ月あたり△日」または「年間△日」</strong>で指定できます。期間の規定休暇は月換算で自動計算されます。
      </p>
      <div className="flex-row" style={{ alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <div className="form-g" style={{ margin: 0, width: 190 }}>
          <label className="form-l">対象期間</label>
          <select className="form-i" value={period} onChange={e => setPeriod(Number(e.target.value))}>
            {PERIOD_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </div>
        <div className="form-g" style={{ margin: 0, width: 140 }}>
          <label className="form-l">規定休日数（日）</label>
          <input className="form-i" type="number" min={0} max={366} step={0.5} value={days} onChange={e => setDays(e.target.value)} />
        </div>
        <button className="btn-p" onClick={save} disabled={saving}>{saving ? '保存中…' : '保存する'}</button>
        {saved && <span style={{ color: '#16a34a', fontSize: 13, fontWeight: 700 }}>✓ 保存しました</span>}
      </div>
      <div style={{ marginTop: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#334155' }}>
        設定内容：<strong>{period === 12 ? '年間' : `${period}ヶ月あたり`} {days || 0}日</strong>
        <span style={{ color: '#64748b' }}>　（≒ 月 {perMonth}日 ／ 年 {perYear}日 相当）</span>
      </div>
      <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: '#94a3b8', alignSelf: 'center' }}>よく使う設定:</span>
        {[
          { d: '9', p: 1, t: '月9日' },
          { d: '108', p: 12, t: '年108日' },
          { d: '120', p: 12, t: '年120日' },
          { d: '30', p: 3, t: '3ヶ月30日' },
        ].map(x => (
          <button key={x.t} className="btn-o" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => { setDays(x.d); setPeriod(x.p); }}>{x.t}</button>
        ))}
      </div>
    </div>
  );
};

export default LeavePage;
